import { useTranslation } from 'react-i18next';
import { Banner, Button, Flex } from '@electricjs/arc';

type CopilotKitInitErrorBannerProps = {
  id: string;
  onRetry: () => void;
};

/**
 * ErrorBoundary fallback for unexpected crashes inside AI chat panels.
 * Used by both the overlay drawer (mobile) and the embedded panel (desktop).
 */
export const AIChatCrashFallback = ({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) => {
  const { t } = useTranslation('ai-chat');

  return (
    <Flex padding="2rem">
      <Banner
        id="copilotkit-drawer-crash-banner"
        intent="error"
        title={t('crashTitle')}
        message={
          t('crashMessage') + (error.message ? ` Error: ${error.message}` : '')
        }
        cta={t('retry')}
        onClickCta={resetErrorBoundary}
      />
    </Flex>
  );
};

export const CopilotKitInitErrorBanner = ({
  id,
  onRetry,
}: CopilotKitInitErrorBannerProps) => {
  const { t } = useTranslation('ai-chat');

  return (
    <Banner
      id={id}
      intent="error"
      title={t('errorTitle', { defaultValue: 'Failed to initialize chat' })}
      message={t('initErrorMessage', {
        defaultValue:
          'We were unable to start a new conversation. Please try again.',
      })}
      cta={
        <Button
          id={`${id}-retry-button`}
          variant="text"
          intent="danger"
          onClick={onRetry}>
          {t('retry', { defaultValue: 'Try Again' })}
        </Button>
      }
    />
  );
};
